"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  SIGNUP_ATTRIBUTION_CONSENT_EVENT,
  buildSignupAttributionTouch,
  formatClearSignupAttributionCookie,
  formatSignupAttributionCookie,
  getSignupAttributionCookieValue,
  mergeSignupAttribution,
  parseSignupAttributionCookie,
} from "@/lib/signup-attribution";

function hasAcceptedConsent(): boolean {
  return /(?:^|; )otr_consent=accepted(?:;|$)/.test(document.cookie);
}

function hasRejectedConsent(): boolean {
  return /(?:^|; )otr_consent=rejected(?:;|$)/.test(document.cookie);
}

function clearAttributionCookie() {
  document.cookie = formatClearSignupAttributionCookie({
    secure: window.location.protocol === "https:",
  });
}

export default function AttributionCapture() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const captureIfAllowed = () => {
      if (hasRejectedConsent()) {
        clearAttributionCookie();
        return;
      }
      if (!hasAcceptedConsent()) return;

      const touch = buildSignupAttributionTouch({
        href: window.location.href,
        referrer: document.referrer,
        siteOrigin: window.location.origin,
      });
      if (!touch) return;

      const current = parseSignupAttributionCookie(
        getSignupAttributionCookieValue(document.cookie),
      );
      const next = mergeSignupAttribution(current, touch);
      if (!next) return;

      document.cookie = formatSignupAttributionCookie(next, {
        secure: window.location.protocol === "https:",
      });
    };

    const handleConsentChange = () => {
      captureIfAllowed();
    };

    captureIfAllowed();
    window.addEventListener(SIGNUP_ATTRIBUTION_CONSENT_EVENT, handleConsentChange);
    return () => {
      window.removeEventListener(SIGNUP_ATTRIBUTION_CONSENT_EVENT, handleConsentChange);
    };
  }, [pathname]);

  return null;
}
